/**
 * Filtro de score con slider dual - V05.2
 * PTEL - Sistema de Normalización de Coordenadas
 */

import React, { memo, useCallback } from 'react';
import { Slider } from '../../ui/slider';

export interface ScoreFilterProps {
  minScore: number;
  maxScore: number;
  onChange: (min: number, max: number) => void;
}

/** Umbrales de score según Design System §7 */
const SCORE_THRESHOLDS = {
  EXCELLENT: 90,
  GOOD: 70,
  FAIR: 50,
};

/** Presets rápidos */
const PRESETS = [
  { label: '90+', min: 90, max: 100, color: 'lime' },
  { label: '70+', min: 70, max: 100, color: 'blue' },
  { label: '50+', min: 50, max: 100, color: 'orange' },
  { label: 'Todos', min: 0, max: 100, color: 'stone' },
] as const;

function getScoreColor(score: number): string {
  if (score >= SCORE_THRESHOLDS.EXCELLENT) return 'text-lime-400';
  if (score >= SCORE_THRESHOLDS.GOOD) return 'text-blue-400';
  if (score >= SCORE_THRESHOLDS.FAIR) return 'text-orange-400';
  return 'text-red-400';
}

export const ScoreFilter = memo<ScoreFilterProps>(function ScoreFilter({
  minScore,
  maxScore,
  onChange,
}) {
  const handleSliderChange = useCallback((values: number[]) => {
    onChange(values[0], values[1]);
  }, [onChange]);

  const handlePreset = useCallback((min: number, max: number) => {
    onChange(min, max);
  }, [onChange]);

  const isPresetActive = useCallback((preset: typeof PRESETS[number]) => {
    return minScore === preset.min && maxScore === preset.max;
  }, [minScore, maxScore]);

  return (
    <div className="ptel-score-filter space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-stone-300">Score de validación</span>
        <div className="flex items-center gap-1 font-mono text-sm">
          <span className={getScoreColor(minScore)}>{minScore}</span>
          <span className="text-stone-500">-</span>
          <span className={getScoreColor(maxScore)}>{maxScore}</span>
        </div>
      </div>

      {/* Slider dual */}
      <Slider
        value={[minScore, maxScore]}
        onValueChange={handleSliderChange}
        min={0}
        max={100}
        step={5}
        className="py-2"
      />

      {/* Leyenda de colores */}
      <div className="flex justify-between text-xs text-stone-500">
        <span className="text-red-400">0</span>
        <span className="text-orange-400">50</span>
        <span className="text-blue-400">70</span>
        <span className="text-lime-400">90</span>
        <span className="text-lime-400">100</span>
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePreset(preset.min, preset.max)}
            className={`
              px-2 py-1 text-xs rounded-md transition-colors
              ${isPresetActive(preset)
                ? 'bg-lime-500/20 text-lime-400 border border-lime-500/30'
                : 'bg-stone-800 text-stone-400 border border-stone-700 hover:bg-stone-700'
              }
            `}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
});

export default ScoreFilter;
